import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  X, Check, Plus, Minus, CaretDown, CaretUp, Trash, MagnifyingGlass, Trophy,
  Timer, Sparkle, ArrowsClockwise, ArrowsDownUp, ArrowUp, ArrowDown,
} from '@phosphor-icons/react';
import Confetti from '../components/Confetti';
import RestTimerSlider from '../components/RestTimerSlider';
import NumericKeypad from '../components/NumericKeypad';
import MuscleIllustration from '../components/MuscleIllustration';
import ExerciseVisual from '../components/ExerciseVisual';
import { WorkoutTimerLarge } from '../components/WorkoutTimer';
import { restChime } from '../utils/restChime';
import { useActiveWorkout } from '../contexts/ActiveWorkoutContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EQUIPMENT_TYPES = ['Barbell', 'Dumbbell', 'Free Weight', 'Machine', 'Cable', 'Bodyweight', 'Cardio - Treadmill', 'Cardio - Outdoor', 'Cardio - Cycle', 'Cardio - Rowing', 'Cardio - Elliptical', 'Kettlebell', 'Band'];
const MUSCLE_GROUPS = ['Chest', 'Shoulders', 'Back', 'Legs', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core', 'Forearms', 'Cardio'];

const isCardio = (exercise) =>
  exercise?.muscle_group === 'Cardio' || (exercise?.equipment_type || '').startsWith('Cardio');

export default function Session() {
  const { workoutId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { elapsedSeconds, refresh: refreshActive } = useActiveWorkout();

  const [workout, setWorkout] = useState(null);
  const [routine, setRoutine] = useState(null);
  const [routineExercises, setRoutineExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [mySets, setMySets] = useState([]);
  const [lastSessionSets, setLastSessionSets] = useState([]);
  const [pbs, setPbs] = useState({});
  const [fatigue, setFatigue] = useState({ last_hit: {} });
  const [expandedId, setExpandedId] = useState(null);
  const [restStart, setRestStart] = useState(null);
  const [restLeft, setRestLeft] = useState(0);
  const [restDuration, setRestDuration] = useState(90);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [swapTargetId, setSwapTargetId] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customPrefillName, setCustomPrefillName] = useState('');
  const [reordering, setReordering] = useState(false);

  // Persist the current exercise list onto the workout so that every
  // session-level tweak (swap / add / remove / reorder / target-sets /
  // bar-weight) survives leaving and returning. Fires with the freshest
  // list on every state update so we never race the local setState.
  const persistSessionExercises = useCallback((list) => {
    if (!workoutId) return;
    const payload = list.map((r) => ({
      exercise_id: r.exercise_id,
      target_sets: r.target_sets,
      bar_weight_kg: r.bar_weight_kg,
      unilateral: r.unilateral || false,
    }));
    axios
      .patch(`${API_URL}/workouts/${workoutId}/exercises`, { exercises: payload }, { withCredentials: true })
      .catch((e) => console.warn('[breth] session exercises persist failed', e));
  }, [workoutId]);

  // Fire the persister once whenever routineExercises changes AFTER the
  // initial load — so consumers below only need to call `setRoutineExercises`
  // and never remember to persist.
  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (!workoutId) return;
    if (!didInitialLoad.current) {
      // Skip the first fire (which is the hydration from server data)
      if (routineExercises.length > 0) didInitialLoad.current = true;
      return;
    }
    persistSessionExercises(routineExercises);
  }, [routineExercises, workoutId, persistSessionExercises]);

  const moveExercise = (idx, delta) => {
    setRoutineExercises((prev) => {
      const to = idx + delta;
      if (to < 0 || to >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[to]] = [copy[to], copy[idx]];
      return copy;
    });
  };
  const [showRoutineRest, setShowRoutineRest] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [goalsByExercise, setGoalsByExercise] = useState({});
  const [barWeightByExercise, setBarWeightByExercise] = useState({});  // exId → kg
  const [editSetId, setEditSetId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [wRes, exRes, pbRes, fatRes, goalsRes] = await Promise.all([
          axios.get(`${API_URL}/workouts/active`, { withCredentials: true }),
          axios.get(`${API_URL}/exercises`, { withCredentials: true }),
          axios.get(`${API_URL}/progress/pbs`, { withCredentials: true }),
          axios.get(`${API_URL}/progress/fatigue`, { withCredentials: true }),
          axios.get(`${API_URL}/goals`, { withCredentials: true }),
        ]);
        if (!wRes.data || wRes.data.id !== workoutId) { navigate('/'); return; }
        setWorkout(wRes.data);
        setAllExercises(exRes.data);
        setFatigue(fatRes.data || { last_hit: {} });
        // Index goals by exercise
        const goalMap = {};
        (goalsRes.data || []).forEach((g) => {
          if (g.achieved) return;
          (goalMap[g.exercise_id] = goalMap[g.exercise_id] || []).push(g);
        });
        setGoalsByExercise(goalMap);
        const pbMap = {};
        pbRes.data.forEach((p) => { pbMap[p.exercise_id] = p; });
        setPbs(pbMap);

        const rRes = await axios.get(`${API_URL}/routines/${wRes.data.routine_id}`, { withCredentials: true });
        setRoutine(rRes.data);
        // If the user has already tweaked this session (swap, add, remove or
        // reorder), the persisted `session_exercises` override wins so the
        // screen matches exactly what they left behind. Fall back to the
        // pristine routine template on first visit.
        const buildFromSession = (list) => {
          const byId = new Map((exRes.data || []).map((e) => [e.id, e]));
          return list
            .filter((e) => byId.has(e.exercise_id))
            .map((e) => ({
              exercise_id: e.exercise_id,
              exercise: byId.get(e.exercise_id),
              target_sets: e.target_sets ?? 3,
              bar_weight_kg: e.bar_weight_kg,
              unilateral: e.unilateral || false,
              _added: true,
            }));
        };
        const persistedSession = Array.isArray(wRes.data.session_exercises) && wRes.data.session_exercises.length > 0
          ? buildFromSession(wRes.data.session_exercises)
          : null;
        const initialExercises = persistedSession || (rRes.data.exercises || []);
        setRoutineExercises(initialExercises);
        // Precedence: routine's rest_seconds > user's default_rest_seconds > 90s
        setRestDuration(
          rRes.data.default_rest_seconds ?? user?.default_rest_seconds ?? 90
        );
        // Load bar weights: prefer routine.exercises[].bar_weight_kg, else localStorage
        try {
          const stored = JSON.parse(localStorage.getItem('breth_bar_weights') || '{}');
          const bwMap = { ...stored };
          (rRes.data.exercises || []).forEach((rex) => {
            if (rex.bar_weight_kg != null) bwMap[rex.exercise_id] = rex.bar_weight_kg;
          });
          setBarWeightByExercise(bwMap);
        } catch (e) { console.warn('[breth] bar weights hydrate failed', e); }

        const setsRes = await axios.get(`${API_URL}/sets/workout/${workoutId}`, { withCredentials: true });
        setMySets(setsRes.data);
        // "Last time" data — now driven by exercise_ids so it covers swaps
        // and cross-routine lifts, not just this routine's history.
        const exIds = initialExercises.map((r) => r.exercise_id).filter(Boolean);
        if (exIds.length > 0) {
          const lastRes = await axios.post(
            `${API_URL}/sets/last-per-exercise`,
            { exercise_ids: exIds },
            { withCredentials: true },
          );
          // Flatten the { exId: [sets] } response into one array — the
          // existing rendering code filters by exercise_id itself.
          const flat = [];
          Object.values(lastRes.data || {}).forEach((arr) => {
            (arr || []).forEach((s) => flat.push(s));
          });
          setLastSessionSets(flat);
        } else {
          setLastSessionSets([]);
        }
        if (initialExercises[0]) setExpandedId(initialExercises[0].exercise_id);
      } catch (e) {
        console.error(e); navigate('/');
      } finally { setLoading(false); }
    })();
  }, [workoutId, navigate]);

  // Refresh "last time" data whenever a new exercise is added or swapped
  // into the active session so its inputs pre-fill correctly.
  const refreshLastPerExercise = useCallback(async (extraIds) => {
    const ids = Array.from(new Set([
      ...routineExercises.map((r) => r.exercise_id),
      ...(extraIds || []),
    ])).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(
        `${API_URL}/sets/last-per-exercise`,
        { exercise_ids: ids },
        { withCredentials: true },
      );
      const flat = [];
      Object.values(res.data || {}).forEach((arr) => {
        (arr || []).forEach((s) => flat.push(s));
      });
      setLastSessionSets(flat);
    } catch (e) {
      console.warn('[breth] last-per-exercise refresh failed', e);
    }
  }, [routineExercises]);

  // Rest tick — also plays the optional chime when the timer hits zero.
  useEffect(() => {
    if (!restStart) return;
    const t = setInterval(() => {
      const elapsed = Math.floor((Date.now() - restStart.start) / 1000);
      const left = restStart.duration - elapsed;
      if (left <= 0) {
        if (user?.rest_timer_sound !== false) {
          // default ON — chime plays unless the user explicitly disabled it
          try { restChime.done(); } catch (e) { /* silent audio failure */ }
        }
        setRestStart(null);
        setRestLeft(0);
      } else {
        setRestLeft(left);
      }
    }, 500);
    return () => clearInterval(t);
  }, [restStart, user?.rest_timer_sound]);

  const logSet = async (exerciseId, setNumber, payload) => {
    try {
      const { data } = await axios.post(`${API_URL}/sets`, {
        workout_id: workoutId, exercise_id: exerciseId, set_number: setNumber,
        is_warmup: false,
        ...payload,
      }, { withCredentials: true });
      setMySets((prev) => [...prev, data]);

      // Update PB map if this is a new PB
      if (data.is_best) {
        setPbs((prev) => ({
          ...prev,
          [exerciseId]: {
            exercise_id: exerciseId,
            weight: data.weight, reps: data.reps,
            set_type: data.set_type || 'weighted',
            duration_seconds: data.duration_seconds,
            distance_m: data.distance_m,
            logged_at: data.logged_at,
          },
        }));
        setConfettiKey((k) => k + 1);
      }

      // Kick rest timer using per-exercise or routine default, unless the
      // user has disabled the rest timer entirely in Settings.
      if (user?.rest_timer_enabled === false) {
        setRestStart(null);
        setRestLeft(0);
      } else {
        const rex = routineExercises.find((r) => r.exercise_id === exerciseId);
        const dur = rex?.rest_seconds || restDuration || 90;
        setRestStart({ start: Date.now(), duration: dur });
        setRestLeft(dur);
      }
    } catch (e) { console.error(e); }
  };

  const removeSet = async (setId) => {
    try {
      await axios.delete(`${API_URL}/sets/${setId}`, { withCredentials: true });
      setMySets((prev) => prev.filter((s) => s.id !== setId));
    } catch (e) { console.error(e); }
  };

  const addExerciseToSession = (exercise) => {
    if (routineExercises.find((e) => e.exercise_id === exercise.id)) {
      setShowAddExercise(false); setExpandedId(exercise.id); return;
    }
    setRoutineExercises((prev) => [
      ...prev,
      { exercise_id: exercise.id, exercise, target_sets: 3, _added: true },
    ]);
    setExpandedId(exercise.id);
    setShowAddExercise(false);
    // Fetch "last time" data for the newly-added exercise so its inputs
    // pre-fill just like a routine exercise would.
    refreshLastPerExercise([exercise.id]);
  };

  const removeExerciseFromSession = async (exerciseId) => {
    // Also delete any sets logged for this exercise in the current workout —
    // otherwise stats/totals stay inflated after the row disappears.
    const setsToDelete = mySets.filter((s) => s.exercise_id === exerciseId);
    try {
      await Promise.all(
        setsToDelete.map((s) =>
          axios.delete(`${API_URL}/sets/${s.id}`, { withCredentials: true })
        )
      );
    } catch (e) { console.warn('[breth] delete sets on exercise removal failed', e); }
    setMySets((prev) => prev.filter((s) => s.exercise_id !== exerciseId));
    setRoutineExercises((prev) => prev.filter((e) => e.exercise_id !== exerciseId));
  };

  const swapExercise = (oldExerciseId, newExercise) => {
    if (newExercise.id === oldExerciseId) {
      // Same exercise picked — treat as a no-op
      setSwapTargetId(null);
      return;
    }
    // If sets already logged against old exercise, warn — those sets stay in DB
    // under the old exercise (visible from Exercises tab history) but disappear
    // from this session card. Confirming is a one-way decision for THIS session.
    const loggedCount = mySets.filter((s) => s.exercise_id === oldExerciseId && !s.is_warmup).length;
    if (loggedCount > 0) {
      if (!window.confirm(
        `You've already logged ${loggedCount} set${loggedCount === 1 ? '' : 's'} for this exercise. ` +
        `Swapping keeps those sets in your history but removes them from this session view. Continue?`
      )) return;
    }
    // If the new exercise is already in the routine, just drop the old row (merge)
    setRoutineExercises((prev) => {
      const existingIdx = prev.findIndex((r) => r.exercise_id === newExercise.id);
      if (existingIdx !== -1) {
        return prev.filter((r) => r.exercise_id !== oldExerciseId);
      }
      return prev.map((r) =>
        r.exercise_id === oldExerciseId
          ? { ...r, exercise_id: newExercise.id, exercise: newExercise, _added: true }
          : r
      );
    });
    setExpandedId(newExercise.id);
    setSwapTargetId(null);
    // Pull the newly-swapped-in exercise's last-session data so its inputs
    // default to the user's most recent numbers.
    refreshLastPerExercise([newExercise.id]);
  };

  const cancelWorkout = async () => {
    if (!window.confirm(
      'Cancel and discard this workout? Every set logged in this session will be permanently deleted.'
    )) return;
    try {
      await axios.delete(`${API_URL}/workouts/${workoutId}`, { withCredentials: true });
      refreshActive();
      navigate('/');
    } catch (e) {
      console.error(e);
      alert('Could not cancel workout');
    }
  };

  const setBarWeight = (exerciseId, kg) => {
    setBarWeightByExercise((prev) => {
      const next = { ...prev, [exerciseId]: kg };
      try { localStorage.setItem('breth_bar_weights', JSON.stringify(next)); }
      catch (e) { console.warn('[breth] bar weight local persist failed', e); }
      return next;
    });
    // Persist onto the routine so it survives across devices
    if (routine?.id) {
      const nextExercises = (routine.exercises || []).map((rex) =>
        rex.exercise_id === exerciseId ? { ...rex, bar_weight_kg: kg } : rex
      );
      axios.put(`${API_URL}/routines/${routine.id}`, { exercises: nextExercises }, { withCredentials: true }).catch(() => {});
    }
  };

  const updateSet = async (setId, payload) => {
    try {
      const { data } = await axios.patch(`${API_URL}/sets/${setId}`, payload, { withCredentials: true });
      setMySets((prev) => prev.map((s) => (s.id === setId ? { ...s, ...data } : s)));
    } catch (e) {
      console.error(e);
      alert('Could not update set');
    }
  };

  const bumpTargetSets = (exerciseId, delta) => {
    setRoutineExercises((prev) =>
      prev.map((r) => r.exercise_id === exerciseId
        ? { ...r, target_sets: Math.max(1, r.target_sets + delta) } : r)
    );
  };

  const setExerciseRest = (exerciseId, secs) => {
    setRoutineExercises((prev) =>
      prev.map((r) => r.exercise_id === exerciseId ? { ...r, rest_seconds: secs } : r)
    );
  };

  const createCustomExercise = async (payload) => {
    try {
      const { data } = await axios.post(`${API_URL}/exercises`, payload, { withCredentials: true });
      setAllExercises((prev) => [...prev, data]);
      addExerciseToSession(data);
      setShowCustomForm(false);
      setCustomPrefillName('');
      // Re-sync the active workout so the elapsed timer keeps ticking off the
      // original started_at (defensive — some flows re-render the header).
      refreshActive();
    } catch (e) { console.error(e); alert('Failed to create'); }
  };

  const finishWorkout = async () => {
    try {
      await axios.post(`${API_URL}/workouts/${workoutId}/finish`, {}, { withCredentials: true });
      refreshActive();
      navigate(`/compare/${workoutId}`);
    } catch (e) { console.error(e); }
  };

  const totals = useMemo(() => {
    const working = mySets.filter((s) => !s.is_warmup);
    return {
      sets: working.length,
      volume: working.reduce((a, s) => a + (s.weight || 0) * (s.reps || 0), 0),
      reps: working.reduce((a, s) => a + (s.reps || 0), 0),
      distance_m: working.reduce((a, s) => a + (s.distance_m || 0), 0),
      duration_s: working.reduce((a, s) => a + (s.duration_seconds || 0), 0),
    };
  }, [mySets]);

  // Cardio-only session? — every routine exercise is cardio (or no weighted exercise)
  const cardioOnly = useMemo(() => {
    if (!routineExercises.length) return false;
    return routineExercises.every((rex) => isCardio(rex.exercise));
  }, [routineExercises]);

  if (loading || !routine) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen">
      <Confetti key={confettiKey} show={confettiKey > 0} onDone={() => {}} />

      <div className="sticky top-0 z-40 bg-bone/90 backdrop-blur-md border-b border-hairline/60">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.24em] text-graphite/50 font-bold">Session</p>
              <h1 className="text-2xl font-black font-heading uppercase text-graphite truncate">
                {routine.name}
              </h1>
              <div className="mt-1.5" data-testid="session-timer-slot">
                <WorkoutTimerLarge seconds={elapsedSeconds} testId="session-timer" />
              </div>
            </div>
            <button
              onClick={() => setShowRoutineRest(true)}
              className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center hover:bg-graphite/5"
              data-testid="routine-rest-button"
              aria-label="Rest timer settings"
            >
              <Timer size={18} weight="bold" />
            </button>
            <button
              onClick={() => {
                if (window.confirm('Quit this session? Sets already logged will be kept.'))
                  navigate('/');
              }}
              className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center hover:bg-graphite/5 ml-2"
              data-testid="close-session-button"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="flex gap-2">
            {cardioOnly ? (
              <>
                <StatTile label="Sets" value={totals.sets} testId="stat-sets" />
                <StatTile label="Distance" value={((totals.distance_m || 0) / 1000).toFixed(2)} unit="km" testId="stat-distance" />
                <StatTile label="Time" value={formatDuration(totals.duration_s)} testId="stat-time" />
              </>
            ) : (
              <>
                <StatTile label="Sets" value={totals.sets} testId="stat-sets" />
                <StatTile label="Reps" value={totals.reps} testId="stat-reps" />
                <StatTile label="Volume" value={Math.round(totals.volume)} unit="kg" testId="stat-volume" />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 py-5 pb-40 space-y-3">
        {/* Reorder toggle — surfaced above the exercise list */}
        {routineExercises.length > 1 && (
          <div className="flex items-center justify-end">
            <button
              onClick={() => setReordering((v) => !v)}
              className={`text-[10px] uppercase tracking-[0.18em] font-black px-3 h-8 rounded-ios-pill flex items-center gap-1.5 ${
                reordering ? 'bg-userRed text-bone' : 'text-graphite/60 bg-graphite/5 hover:bg-graphite/10'
              }`}
              data-testid="reorder-session-button"
            >
              <ArrowsDownUp size={12} weight="bold" />
              {reordering ? 'Done' : 'Reorder'}
            </button>
          </div>
        )}

        {routineExercises.map((rex, idx) => {
          const exercise = rex.exercise;
          const cardio = isCardio(exercise);
          const exSets = mySets.filter((s) => s.exercise_id === exercise.id && !s.is_warmup);
          const exLast = lastSessionSets.filter((s) => s.exercise_id === exercise.id);
          const isExpanded = expandedId === exercise.id;
          const pb = pbs[exercise.id];
          // Fatigue tier for this exercise's muscle group (0=fresh, 1=recovering, 2=today)
          const lastHitIso = fatigue.last_hit?.[exercise.muscle_group];
          let fatigueTier = 0;
          if (lastHitIso) {
            const hrs = Math.max(0, (Date.now() - new Date(lastHitIso).getTime()) / 36e5);
            fatigueTier = hrs < 24 ? 2 : hrs < 72 ? 1 : 0;
          }

          return (
            <ExerciseCard
              key={exercise.id}
              exercise={exercise}
              targetSets={rex.target_sets}
              restSeconds={rex.rest_seconds}
              cardio={cardio}
              addedInSession={rex._added}
              expanded={isExpanded}
              onToggle={() => setExpandedId(isExpanded ? null : exercise.id)}
              sets={exSets}
              lastSets={exLast}
              pb={pb}
              fatigueTier={fatigueTier}
              goals={goalsByExercise[exercise.id] || []}
              barWeight={barWeightByExercise[exercise.id]}
              onSetBarWeight={(kg) => setBarWeight(exercise.id, kg)}
              onEditSet={(setId) => setEditSetId(setId)}
              onLog={(setNum, payload) => logSet(exercise.id, setNum, payload)}
              onRemoveSet={removeSet}
              onRemoveExercise={() => removeExerciseFromSession(exercise.id)}
              onSwapExercise={() => setSwapTargetId(exercise.id)}
              onBumpTarget={(d) => bumpTargetSets(exercise.id, d)}
              onSetRest={(s) => setExerciseRest(exercise.id, s)}
              routineDefaultRest={restDuration}
              reordering={reordering}
              canMoveUp={idx > 0}
              canMoveDown={idx < routineExercises.length - 1}
              onMoveUp={() => moveExercise(idx, -1)}
              onMoveDown={() => moveExercise(idx, 1)}
            />
          );
        })}

        <button
          onClick={() => setShowAddExercise(true)}
          className="w-full h-14 border-2 border-dashed border-graphite/25 text-graphite/60 hover:text-graphite hover:border-graphite/50 font-bold text-sm uppercase tracking-[0.15em] rounded-ios-lg flex items-center justify-center gap-2"
          data-testid="add-exercise-button"
        >
          <Plus size={18} weight="bold" />
          Add Exercise
        </button>

        <button
          onClick={finishWorkout}
          className="w-full h-16 bg-graphite text-bone font-black text-base uppercase tracking-[0.15em] rounded-ios-pill mt-6"
          data-testid="finish-workout-button"
        >
          Finish & Compare
        </button>

        {/* Small, hard-to-hit-accidentally cancel action */}
        <div className="text-center pt-8 pb-2">
          <button
            onClick={cancelWorkout}
            className="text-[10px] uppercase tracking-[0.18em] font-semibold text-graphite/35 hover:text-userRed underline underline-offset-4 decoration-graphite/20 hover:decoration-userRed"
            data-testid="cancel-workout-button"
          >
            Cancel Workout
          </button>
        </div>
      </div>

      {/* Rest timer floating — portaled to body so `position: fixed` isn't
          trapped inside any transformed ancestor (wiggle-item / will-change). */}
      {restStart && restLeft > 0 && createPortal(
        <div className="rest-timer-bar animate-slide-up" data-testid="rest-timer-bar">
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.24em] text-graphite/60 font-bold mb-1.5">
                Rest
              </p>
              <div className="h-1.5 bg-hairline rounded-full overflow-hidden">
                <div
                  className="h-full bg-userRed rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${(restLeft / restStart.duration) * 100}%` }}
                />
              </div>
            </div>
            <div className="text-3xl font-black font-heading tabular-nums text-graphite min-w-[2.5rem] text-right">
              {restLeft}
            </div>
            <button
              onClick={() => { setRestStart(null); setRestLeft(0); }}
              className="text-[11px] uppercase tracking-[0.15em] font-bold text-graphite/50 hover:text-graphite px-2"
              data-testid="skip-rest-button"
            >
              Skip
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Routine-wide rest timer settings */}
      {showRoutineRest && (
        <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
          <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
            <div className="px-5 py-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-black font-heading uppercase text-graphite">
                  Routine Rest
                </h2>
                <button
                  onClick={() => setShowRoutineRest(false)}
                  className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
                  data-testid="close-routine-rest-button"
                >
                  <X size={18} weight="bold" />
                </button>
              </div>
              <p className="text-xs text-graphite/60">
                Applies to every set unless an exercise has its own rest set.
              </p>
              <RestTimerSlider
                value={restDuration}
                onChange={setRestDuration}
                testIdPrefix="routine-rest"
              />
              {user?.default_rest_seconds && restDuration !== user.default_rest_seconds && (
                <button
                  onClick={() => setRestDuration(user.default_rest_seconds)}
                  className="w-full h-10 rounded-ios-pill border border-hairline text-[11px] uppercase tracking-[0.18em] font-bold text-graphite/70 hover:bg-graphite/5"
                  data-testid="reset-to-default-rest-button"
                >
                  Reset to your default ({user.default_rest_seconds}s)
                </button>
              )}
              <button
                onClick={() => setShowRoutineRest(false)}
                className="w-full h-12 bg-userRed text-bone font-bold text-sm uppercase tracking-[0.15em] rounded-ios-pill"
                data-testid="save-routine-rest-button"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddExercise && (
        <ExercisePicker
          exercises={allExercises}
          existingIds={routineExercises.map((r) => r.exercise_id)}
          onPick={addExerciseToSession}
          onCustom={(prefillName) => { setCustomPrefillName(prefillName || ''); setShowAddExercise(false); setShowCustomForm(true); }}
          onClose={() => setShowAddExercise(false)}
          title="Add Exercise"
        />
      )}

      {swapTargetId && (
        <ExercisePicker
          exercises={allExercises}
          existingIds={[swapTargetId]}  /* exclude the exercise being swapped OUT */
          onPick={(ex) => swapExercise(swapTargetId, ex)}
          onCustom={() => setSwapTargetId(null)}
          onClose={() => setSwapTargetId(null)}
          title="Swap Exercise"
        />
      )}

      {editSetId && (() => {
        const s = mySets.find((x) => x.id === editSetId);
        if (!s) return null;
        const cardio = s.set_type === 'cardio';
        return (
          <SetEditModal
            set={s}
            cardio={cardio}
            onSave={async (payload) => { await updateSet(editSetId, payload); setEditSetId(null); }}
            onClose={() => setEditSetId(null)}
          />
        );
      })()}

      {showCustomForm && (
        <CustomExerciseModal
          initialName={customPrefillName}
          onCreate={createCustomExercise}
          onClose={() => { setShowCustomForm(false); setCustomPrefillName(''); }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, unit, testId }) {
  return (
    <div className="flex-1 ios-card px-3 py-2" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">{label}</p>
      <p className="text-2xl font-black font-heading tabular-nums text-graphite leading-tight mt-1">
        {value}{unit && <span className="text-xs font-normal ml-0.5">{unit}</span>}
      </p>
    </div>
  );
}

function ExerciseCard({
  exercise, targetSets, restSeconds, cardio, addedInSession,
  expanded, onToggle, sets, lastSets, pb, fatigueTier = 0,
  goals = [], barWeight, onSetBarWeight, onEditSet,
  onLog, onRemoveSet, onRemoveExercise, onSwapExercise, onBumpTarget, onSetRest,
  routineDefaultRest = 90,
  reordering = false, canMoveUp = false, canMoveDown = false, onMoveUp, onMoveDown,
}) {
  const done = sets.length;
  const complete = done >= targetSets;
  const [showExRest, setShowExRest] = useState(false);

  // Fatigue outline — very subtle, more friendly reminder than warning
  const fatigueStyle = fatigueTier === 2
    ? { boxShadow: 'inset 0 0 0 1px rgba(192,57,47,0.28)' }
    : fatigueTier === 1
      ? { boxShadow: 'inset 0 0 0 1px rgba(192,57,47,0.14)' }
      : {};

  return (
    <div
      className={`ios-card overflow-hidden ${complete ? 'border-userRed/40' : ''}`}
      style={fatigueStyle}
      data-testid={`exercise-card-${exercise.id}`}
      data-fatigue={fatigueTier}
    >
      {fatigueTier > 0 && (
        <div
          className={`px-4 py-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${
            fatigueTier === 2 ? 'text-userRed/70 bg-userRed/[0.04]' : 'text-userRed/55 bg-userRed/[0.025]'
          }`}
          data-testid={`fatigue-hint-${exercise.id}`}
        >
          <span className={`w-1 h-1 rounded-full ${fatigueTier === 2 ? 'bg-userRed/60' : 'bg-userRed/35'}`} />
          {fatigueTier === 2
            ? `${exercise.muscle_group} hit today · take it easy`
            : `${exercise.muscle_group} still recovering`}
        </div>
      )}
      <div className="flex items-stretch">
        <button
          onClick={onToggle}
          className="flex-1 px-5 py-4 flex items-center justify-between text-left"
          data-testid={`exercise-toggle-${exercise.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="text-lg font-black font-heading uppercase text-graphite truncate">
                {exercise.name}
              </h3>
              {cardio && (
                <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-partnerBlue bg-partnerBlue/10 px-1.5 py-0.5 rounded-ios-pill">
                  Cardio
                </span>
              )}
              {addedInSession && (
                <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-userRed bg-userRed/10 px-1.5 py-0.5 rounded-ios-pill">
                  Added
                </span>
              )}
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-graphite/50 font-semibold">
              {cardio
                ? (exercise.equipment_type || '').replace(/^Cardio\s*[-·]\s*/i, '').trim() || 'Cardio'
                : `${exercise.muscle_group} · ${exercise.equipment_type}`}
            </p>
          </div>
          <div className="flex items-center gap-2 pl-2">
            <div className={`text-xl font-black font-heading tabular-nums ${complete ? 'text-userRed' : 'text-graphite'}`}>
              {done}<span className="text-graphite/40">/{targetSets}</span>
            </div>
            {expanded ? <CaretUp size={18} weight="bold" /> : <CaretDown size={18} weight="bold" />}
          </div>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onSwapExercise(); }}
          className="px-3 border-l border-hairline/50 text-graphite/50 hover:text-userRed hover:bg-graphite/[0.03] flex items-center justify-center"
          data-testid={`swap-exercise-${exercise.id}`}
          aria-label="Swap exercise"
          title="Swap this exercise for another"
        >
          <ArrowsClockwise size={16} weight="bold" />
        </button>
        {reordering && (
          <div className="flex flex-col border-l border-hairline/50 flex-shrink-0" data-testid={`reorder-controls-${exercise.id}`}>
            <button
              onClick={(e) => { e.stopPropagation(); onMoveUp && onMoveUp(); }}
              disabled={!canMoveUp}
              className="flex-1 w-11 flex items-center justify-center text-userRed disabled:text-graphite/20 hover:bg-userRed/10"
              data-testid={`move-exercise-up-${exercise.id}`}
              aria-label="Move up"
            >
              <ArrowUp size={14} weight="bold" />
            </button>
            <div className="h-px bg-hairline/50" />
            <button
              onClick={(e) => { e.stopPropagation(); onMoveDown && onMoveDown(); }}
              disabled={!canMoveDown}
              className="flex-1 w-11 flex items-center justify-center text-userRed disabled:text-graphite/20 hover:bg-userRed/10"
              data-testid={`move-exercise-down-${exercise.id}`}
              aria-label="Move down"
            >
              <ArrowDown size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="px-5 pb-5 animate-slide-up">
          {/* Exercise visual — real photo when we have one, muscle-group
              SVG otherwise. Provides a quick "how it looks" cue. */}
          <div className="flex items-center gap-3 -mt-1 mb-3 pb-3 border-b border-hairline/50">
            <ExerciseVisual
              exercise={exercise}
              size={64}
              testId={`illustration-${exercise.id}`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">
                Targets
              </p>
              <p className="text-sm font-black uppercase tracking-tight text-graphite mt-0.5 leading-tight">
                {cardio ? 'Aerobic system · Legs' : exercise.muscle_group}
              </p>
              <p className="text-[10px] text-graphite/50 uppercase tracking-[0.14em] font-semibold mt-0.5 leading-none">
                {exercise.equipment_type}
              </p>
            </div>
          </div>

          {/* Active goal(s) callout — the whole point of setting one is to see it in the moment */}
          {goals.length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid={`session-goals-${exercise.id}`}>
              {goals.slice(0, 2).map((g) => {
                const unit = g.metric === 'one_rm' || g.metric === 'top_set_weight' ? 'kg'
                  : g.metric === 'distance_m' ? 'm' : 's';
                const pct = Math.min(100, Math.max(3, g.pct || 0));
                return (
                  <div key={g.id} className="px-3 py-2 bg-userRed/6 border border-userRed/25 rounded-ios" data-testid={`session-goal-${g.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-userRed">
                        Goal · {g.metric === 'one_rm' ? 'Est. 1RM' : g.metric === 'top_set_weight' ? 'Top Set' : g.metric === 'distance_m' ? 'Distance' : 'Duration'}
                      </p>
                      <p className="text-sm font-black font-heading tabular-nums text-graphite">
                        {g.current_value}<span className="text-graphite/40 font-normal text-xs">/{g.target_value}{unit}</span>
                      </p>
                    </div>
                    <div className="h-1.5 bg-graphite/8 rounded-ios-pill overflow-hidden">
                      <div className="h-full bg-userRed rounded-ios-pill transition-all duration-700" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* PB — inline one-liner, no card */}
          {pb && (
            <p
              className="text-[11px] font-bold uppercase tracking-[0.14em] text-userRed mb-2 flex items-center gap-1.5"
              data-testid={`pb-inline-${exercise.id}`}
            >
              <Trophy size={11} weight="fill" />
              PB {pb.set_type === 'cardio'
                ? `${((pb.distance_m || 0) / 1000).toFixed(2)}km · ${formatDuration(pb.duration_seconds)}`
                : `${pb.weight}kg × ${pb.reps}`}
            </p>
          )}

          {/* Target + Rest — Rest is icon-only until tapped */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="flex items-center justify-between px-3 py-2 bg-graphite/5 rounded-ios">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/60">
                Target
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => onBumpTarget(-1)}
                  disabled={targetSets <= 1 || targetSets <= done}
                  className="w-7 h-7 rounded-ios-pill bg-bone border border-hairline flex items-center justify-center disabled:opacity-30"
                  data-testid={`target-minus-${exercise.id}`}
                >
                  <Minus size={12} weight="bold" />
                </button>
                <span className="text-lg font-black font-heading tabular-nums text-graphite w-5 text-center">
                  {targetSets}
                </span>
                <button
                  onClick={() => onBumpTarget(1)}
                  className="w-7 h-7 rounded-ios-pill bg-bone border border-hairline flex items-center justify-center"
                  data-testid={`target-plus-${exercise.id}`}
                >
                  <Plus size={12} weight="bold" />
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowExRest(!showExRest)}
              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-ios ${
                restSeconds ? 'bg-userRed/10 text-userRed' : 'bg-graphite/5 text-graphite/70'
              }`}
              data-testid={`exercise-rest-button-${exercise.id}`}
              aria-label={restSeconds ? `Rest ${restSeconds}s` : 'Rest'}
            >
              <Timer size={16} weight="bold" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold">
                Rest
              </span>
            </button>
          </div>

          {showExRest && (
            <div className="mb-3 p-3 bg-boneWarm rounded-ios border border-hairline animate-slide-up">
              <RestTimerSlider
                value={restSeconds || 90}
                onChange={onSetRest}
                testIdPrefix={`ex-rest-${exercise.id}`}
              />
              {restSeconds && (
                <button
                  onClick={() => { onSetRest(null); setShowExRest(false); }}
                  className="mt-2 text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/50 hover:text-userRed"
                  data-testid={`clear-ex-rest-${exercise.id}`}
                >
                  Clear override
                </button>
              )}
            </div>
          )}

          {/* Set rows: logged + quick-repeat-from-last */}
          <div className="space-y-2">
            {sets.map((s, i) => (
              <LoggedSetRow
                key={s.id}
                setNum={i + 1}
                s={s}
                cardio={cardio}
                onRemove={onRemoveSet}
                onEdit={() => onEditSet(s.id)}
                justLogged={i === sets.length - 1 && (Date.now() - new Date(s.logged_at || s.created_at || 0).getTime()) < 1500}
              />
            ))}

            {/* Active input for next set */}
            <SetInput
              setNum={done + 1}
              cardio={cardio}
              defaultWeight={sets.length > 0 ? sets[sets.length - 1].weight : lastSets[done]?.weight || 20}
              defaultReps={sets.length > 0 ? sets[sets.length - 1].reps : lastSets[done]?.reps || 8}
              defaultDuration={sets.length > 0 ? sets[sets.length - 1].duration_seconds : lastSets[done]?.duration_seconds || 600}
              defaultDistance={sets.length > 0 ? sets[sets.length - 1].distance_m : lastSets[done]?.distance_m || 1000}
              equipmentType={exercise.equipment_type}
              barWeightState={barWeight}
              onChangeBarWeight={onSetBarWeight}
              onLog={(payload) => onLog(done + 1, payload)}
            />
          </div>

          <RemoveExerciseButton
            exerciseId={exercise.id}
            hasSets={sets.length > 0}
            onConfirm={onRemoveExercise}
          />
        </div>
      )}
    </div>
  );
}

/* Two-tap destructive control. First tap arms it (red X + confirmation label),
   second tap actually removes the exercise (and any sets logged for it). */
function RemoveExerciseButton({ exerciseId, hasSets, onConfirm }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);
  const label = armed
    ? (hasSets ? 'Tap again — will delete sets' : 'Tap again to remove')
    : 'Remove this exercise';
  return (
    <button
      onClick={() => (armed ? onConfirm() : setArmed(true))}
      className={`w-full text-[11px] uppercase tracking-[0.18em] font-bold py-2 mt-3 rounded-ios-pill flex items-center justify-center gap-2 transition-colors ${
        armed
          ? 'text-bone bg-userRed'
          : 'text-graphite/40 hover:text-userRed border border-hairline'
      }`}
      data-testid={`remove-exercise-${exerciseId}`}
      aria-label={label}
    >
      {armed ? <X size={12} weight="bold" /> : <Trash size={11} weight="bold" />}
      {label}
    </button>
  );
}

function LoggedSetRow({ setNum, s, cardio, onRemove, onEdit, justLogged = false }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 2500);
    return () => clearTimeout(t);
  }, [armed]);

  const handleDelete = () => {
    if (!armed) {
      setArmed(true);
      return;
    }
    onRemove(s.id);
  };

  return (
    <div
      className={`flex items-center gap-3 py-2 border-b border-hairline/50 last:border-0 ${
        justLogged ? 'set-completed-flash' : 'set-pop'
      }`}
      data-testid={`logged-set-${s.id}`}
    >
      {justLogged ? (
        <span className="w-6 flex items-center justify-center text-userRed check-pop" aria-hidden>
          <Check size={14} weight="bold" />
        </span>
      ) : (
        <span className="text-[10px] uppercase tracking-[0.18em] font-black text-userRed w-6 tabular-nums">
          {setNum}
        </span>
      )}
      <button
        type="button"
        onClick={onEdit}
        className="flex-1 text-left text-lg font-black font-heading tabular-nums text-graphite hover:text-userRed leading-none"
        data-testid={`edit-set-${s.id}`}
        title="Tap to edit"
      >
        {cardio || s.set_type === 'cardio'
          ? <>{((s.distance_m || 0) / 1000).toFixed(2)}<span className="text-xs font-normal text-graphite/60">km · {formatDuration(s.duration_seconds)}</span></>
          : <>{s.weight}<span className="text-xs font-normal text-graphite/60">kg</span> × {s.reps}</>}
      </button>
      {s.rpe && (
        <span className="text-[10px] font-bold text-userRed tabular-nums">
          RPE {s.rpe}
        </span>
      )}
      {s.is_best && (
        <span className="text-[9px] uppercase tracking-[0.14em] font-black text-bone bg-userRed px-1.5 py-0.5 rounded-ios-pill">
          PB
        </span>
      )}
      <button
        onClick={handleDelete}
        className={`w-7 h-7 flex items-center justify-center transition-colors ${
          armed ? 'text-bone bg-userRed rounded-full' : 'text-graphite/30 hover:text-userRed'
        }`}
        data-testid={`remove-set-${s.id}`}
        aria-label={armed ? 'Tap again to delete' : 'Delete set'}
        title={armed ? 'Tap again to delete' : 'Delete'}
      >
        {armed ? <X size={13} weight="bold" /> : <Trash size={12} weight="bold" />}
      </button>
    </div>
  );
}

function SetInput({ setNum, cardio, defaultWeight, defaultReps, defaultDuration, defaultDistance, equipmentType, onLog, barWeightState, onChangeBarWeight }) {
  const [weight, setWeight] = useState(defaultWeight);
  const [reps, setReps] = useState(defaultReps);
  const [duration, setDuration] = useState(defaultDuration);
  const [distance, setDistance] = useState(defaultDistance);
  const [rpe, setRpe] = useState(null);
  const [showRpe, setShowRpe] = useState(false);

  const submit = () => {
    if (cardio) {
      onLog({
        weight: 0, reps: 0,
        set_type: 'cardio',
        duration_seconds: duration,
        distance_m: distance,
        rpe,
      });
    } else {
      onLog({ weight, reps, set_type: 'weighted', rpe });
    }
    setRpe(null);
    setShowRpe(false);
  };

  return (
    <div className="border-2 border-userRed/60 bg-boneWarm rounded-ios p-4 space-y-3 relative">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-userRed">
          Set {setNum} — Now
        </p>
      </div>

      {cardio ? (
        <div className="grid grid-cols-2 gap-2">
          <Stepper label="Distance (optional)" value={distance} unit="m" step={100} onChange={setDistance} testId={`distance-${setNum}`} quickValues={[0, 500, 1000, 2000, 5000]} />
          <DurationStepper label="Time" value={duration} onChange={setDuration} testId={`duration-${setNum}`} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Stepper label="Weight" value={weight} unit="kg" step={2.5} onChange={setWeight} testId={`weight-${setNum}`} allowDecimal quickValues={[20, 40, 60, 80, 100]} />
          <Stepper label="Reps" value={reps} step={1} onChange={setReps} testId={`reps-${setNum}`} allowDecimal={false} quickValues={[5, 6, 8, 10, 12, 15]} />
        </div>
      )}

      {equipmentType === 'Barbell' && !cardio && <PlateCalculator weight={weight} barWeightState={barWeightState} onChangeBarWeight={onChangeBarWeight} />}

      {/* Log + compact RPE */}
      <div className="flex gap-2 items-stretch">
        <button
          onClick={submit}
          className="flex-1 h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill flex items-center justify-center gap-2"
          data-testid={`log-set-${setNum}`}
        >
          <Check size={18} weight="bold" />
          Log Set
        </button>
        <button
          onClick={() => setShowRpe(!showRpe)}
          className={`h-14 min-w-[54px] px-3 rounded-ios-pill text-xs font-black uppercase tracking-[0.15em] flex items-center justify-center border-2 ${
            rpe ? 'bg-userRed/15 border-userRed text-userRed' : 'border-hairline text-graphite/50'
          }`}
          data-testid={`rpe-toggle-${setNum}`}
        >
          {rpe ? `RPE ${rpe}` : 'RPE'}
        </button>
      </div>

      {showRpe && (
        <div className="animate-slide-up p-2 bg-bone rounded-ios border border-hairline">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
            How hard was it?
          </p>
          <div className="grid grid-cols-6 gap-1.5">
            {[5, 6, 7, 8, 9, 10].map((n) => (
              <button
                key={n}
                onClick={() => { setRpe(n); setShowRpe(false); }}
                className={`h-9 rounded-ios-pill text-sm font-black tabular-nums font-heading ${
                  rpe === n ? 'bg-userRed text-bone' : 'bg-graphite/5 text-graphite/60'
                }`}
                data-testid={`rpe-pick-${setNum}-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stepper({ label, value, unit, step, onChange, testId, allowDecimal = true, quickValues = [] }) {
  const [showKeypad, setShowKeypad] = useState(false);
  return (
    <div className="bg-bone border border-hairline rounded-ios p-2.5">
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1">{label}</p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, +(value - step).toFixed(2)))}
          className="w-8 h-8 rounded-ios-pill bg-graphite/5 hover:bg-graphite/10 flex items-center justify-center"
          data-testid={`${testId}-minus`}
        >
          <Minus size={14} weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => setShowKeypad(true)}
          className="flex-1 text-center leading-none py-2 rounded-ios hover:bg-graphite/5 active:bg-graphite/10"
          data-testid={`${testId}-tap`}
          aria-label={`Enter ${label}`}
        >
          <span className="text-2xl font-black font-heading tabular-nums text-graphite">{value}</span>
          {unit && <span className="text-xs font-normal ml-0.5">{unit}</span>}
        </button>
        <button
          type="button"
          onClick={() => onChange(+(value + step).toFixed(2))}
          className="w-8 h-8 rounded-ios-pill bg-graphite/5 hover:bg-graphite/10 flex items-center justify-center"
          data-testid={`${testId}-plus`}
        >
          <Plus size={14} weight="bold" />
        </button>
      </div>
      {showKeypad && (
        <NumericKeypad
          title={label}
          unit={unit || ''}
          initial={value}
          allowDecimal={allowDecimal}
          quickValues={quickValues}
          onSubmit={(v) => { onChange(v); setShowKeypad(false); }}
          onClose={() => setShowKeypad(false)}
          testIdPrefix={`keypad-${testId}`}
        />
      )}
    </div>
  );
}

function DurationStepper({ label, value, onChange, testId }) {
  const [showKeypad, setShowKeypad] = useState(false);
  return (
    <div className="bg-bone border border-hairline rounded-ios p-2.5">
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1">{label}</p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 60))}
          className="w-8 h-8 rounded-ios-pill bg-graphite/5 flex items-center justify-center"
          data-testid={`${testId}-minus`}
        >
          <Minus size={14} weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => setShowKeypad(true)}
          className="flex-1 text-center leading-none py-1 rounded-md hover:bg-graphite/[0.03] active:bg-graphite/[0.06]"
          data-testid={`${testId}-tap`}
          aria-label="Edit time"
        >
          <span className="text-2xl font-black font-heading tabular-nums text-graphite">
            {formatDuration(value)}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onChange(value + 60)}
          className="w-8 h-8 rounded-ios-pill bg-graphite/5 flex items-center justify-center"
          data-testid={`${testId}-plus`}
        >
          <Plus size={14} weight="bold" />
        </button>
      </div>
      {showKeypad && (
        <NumericKeypad
          title="Time (seconds)"
          unit="s"
          initial={value}
          quickValues={[60, 180, 300, 600, 900, 1800]}
          allowDecimal={false}
          onSubmit={(v) => { onChange(Math.max(0, Math.round(v))); setShowKeypad(false); }}
          onClose={() => setShowKeypad(false)}
          testIdPrefix={`keypad-${testId}`}
        />
      )}
    </div>
  );
}

const BAR_PRESETS = [
  { name: 'Olympic', kg: 20 },
  { name: 'Womens Bar', kg: 15 },
  { name: 'EZ Bar', kg: 10 },
  { name: 'Smith', kg: 15 },
  { name: 'Fixed', kg: 0 },
];

function PlateCalculator({ weight, barWeightState, onChangeBarWeight }) {
  const [showPicker, setShowPicker] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const barWeight = barWeightState ?? 20;
  const perSide = (weight - barWeight) / 2;

  const plates = [
    { w: 25, c: '#C0392F', dark: false },
    { w: 20, c: '#1E5AA0', dark: false },
    { w: 15, c: '#F39C12', dark: false },
    { w: 10, c: '#27AE60', dark: false },
    { w: 5, c: '#F4F1EA', dark: true },
    { w: 2.5, c: '#191C21', dark: false },
    { w: 1.25, c: '#95A5A6', dark: true },
  ];
  let remaining = perSide;
  const needed = [];
  if (perSide > 0) {
    for (const p of plates) {
      while (remaining >= p.w - 0.001) {
        needed.push(p);
        remaining = Math.round((remaining - p.w) * 100) / 100;
      }
    }
  }

  const currentPreset = BAR_PRESETS.find((p) => p.kg === barWeight);
  const currentLabel = currentPreset ? currentPreset.name : `${barWeight}kg`;

  return (
    <div className="bg-graphite/5 rounded-ios px-3 py-2">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50">
          Per side
        </p>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-userRed hover:opacity-70 flex items-center gap-1"
          data-testid="bar-weight-toggle"
        >
          Bar: {currentLabel} · {barWeight}kg
        </button>
      </div>

      {showPicker && (
        <div className="mb-2 p-2 bg-bone rounded-ios border border-hairline animate-slide-up">
          <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
            Calibrate base weight
          </p>
          <div className="flex flex-wrap gap-1.5">
            {BAR_PRESETS.map((p) => (
              <button
                key={p.name}
                onClick={() => { onChangeBarWeight(p.kg); setShowPicker(false); }}
                className={`h-8 px-2.5 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                  barWeight === p.kg ? 'bg-graphite text-bone' : 'bg-graphite/8 text-graphite/70'
                }`}
                data-testid={`bar-preset-${p.name.toLowerCase().replace(/[^a-z]/g,'-')}`}
              >
                {p.name} · {p.kg}kg
              </button>
            ))}
            <button
              onClick={() => setShowCustom(true)}
              className="h-8 px-2.5 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold bg-userRed/10 text-userRed"
              data-testid="bar-custom-button"
            >
              Custom
            </button>
          </div>
        </div>
      )}

      {perSide > 0 && needed.length > 0 ? (
        <div className="flex gap-1 flex-wrap items-center">
          {needed.map((p, i) => (
            <div key={`${p.w}-${i}`}
              className="text-[10px] font-black tabular-nums px-2 py-1 rounded-ios-pill"
              style={{ backgroundColor: p.c, color: p.dark ? '#191C21' : '#EDEAE3', border: p.w === 5 ? '1px solid #DCD7CB' : 'none' }}>
              {p.w}
            </div>
          ))}
          <span className="text-[10px] font-bold text-graphite/50 tabular-nums ml-1">
            = {perSide}kg / side
          </span>
        </div>
      ) : perSide === 0 ? (
        <p className="text-[10px] font-bold text-graphite/50">Just the bar</p>
      ) : (
        <p className="text-[10px] font-bold text-userRed">
          Target &lt; bar weight — adjust bar or target
        </p>
      )}

      {showCustom && (
        <NumericKeypad
          title="Bar Weight"
          unit="kg"
          initial={barWeight}
          allowDecimal
          quickValues={[5, 7, 10, 15, 20, 25]}
          onSubmit={(v) => { onChangeBarWeight(v); setShowCustom(false); setShowPicker(false); }}
          onClose={() => setShowCustom(false)}
          testIdPrefix="bar-custom-keypad"
        />
      )}
    </div>
  );
}

function formatDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

function SetEditModal({ set, cardio, onSave, onClose }) {
  const [weight, setWeight] = useState(set.weight || 0);
  const [reps, setReps] = useState(set.reps || 0);
  const [dur, setDur] = useState(set.duration_seconds || 0);
  const [dist, setDist] = useState(set.distance_m || 0);
  const [rpe, setRpe] = useState(set.rpe || null);
  const [active, setActive] = useState(null); // 'weight'|'reps'|'dur'|'dist'

  // Hide the persistent bottom nav while the edit modal is open so it
  // never overlaps the Save Changes button or the on-screen keypad.
  useEffect(() => {
    document.body.classList.add('modal-nav-hidden');
    return () => document.body.classList.remove('modal-nav-hidden');
  }, []);

  const save = () => {
    if (cardio) onSave({ duration_seconds: dur, distance_m: dist, rpe });
    else onSave({ weight, reps, rpe });
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up max-h-[92vh] flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-hairline flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 leading-none">Edit</p>
            <h2 className="text-2xl font-black font-heading uppercase text-graphite leading-none mt-0.5">Set #{set.set_number}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
            data-testid="close-set-edit-modal"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="px-5 pt-4 pb-3 grid grid-cols-2 gap-2">
          {cardio ? (
            <>
              <EditField label="Distance" value={dist} unit="m" onTap={() => setActive('dist')} testId="edit-distance" />
              <EditField label="Time" value={formatDuration(dur)} unit="" onTap={() => setActive('dur')} testId="edit-duration" />
            </>
          ) : (
            <>
              <EditField label="Weight" value={weight} unit="kg" onTap={() => setActive('weight')} testId="edit-weight" />
              <EditField label="Reps" value={reps} unit="" onTap={() => setActive('reps')} testId="edit-reps" />
            </>
          )}
        </div>
        {/* RPE row */}
        <div className="px-5 pb-3">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">RPE</p>
          <div className="grid grid-cols-6 gap-1.5">
            {[5,6,7,8,9,10].map((n) => (
              <button
                key={n}
                onClick={() => setRpe(rpe === n ? null : n)}
                className={`h-10 rounded-ios-pill text-sm font-black tabular-nums font-heading ${
                  rpe === n ? 'bg-userRed text-bone shadow-ios' : 'bg-graphite/5 text-graphite/60'
                }`}
                data-testid={`edit-rpe-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={save}
          className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-2"
          data-testid="save-set-edit"
        >
          <Check size={16} weight="bold" />
          Save Changes
        </button>
      </div>

      {active === 'weight' && (
        <NumericKeypad title="Weight" unit="kg" initial={weight} allowDecimal quickValues={[20,40,60,80,100]}
          onSubmit={(v) => { setWeight(v); setActive(null); }} onClose={() => setActive(null)} testIdPrefix="edit-weight-keypad" />
      )}
      {active === 'reps' && (
        <NumericKeypad title="Reps" initial={reps} quickValues={[5,6,8,10,12,15]}
          onSubmit={(v) => { setReps(Math.round(v)); setActive(null); }} onClose={() => setActive(null)} testIdPrefix="edit-reps-keypad" />
      )}
      {active === 'dist' && (
        <NumericKeypad title="Distance" unit="m" initial={dist} allowDecimal quickValues={[500,1000,2000,5000]}
          onSubmit={(v) => { setDist(v); setActive(null); }} onClose={() => setActive(null)} testIdPrefix="edit-dist-keypad" />
      )}
      {active === 'dur' && (
        <NumericKeypad title="Duration (seconds)" unit="s" initial={dur} quickValues={[60,180,300,600,1200]}
          onSubmit={(v) => { setDur(Math.round(v)); setActive(null); }} onClose={() => setActive(null)} testIdPrefix="edit-dur-keypad" />
      )}
    </div>,
    document.body
  );
}

function EditField({ label, value, unit, onTap, testId }) {
  return (
    <button
      type="button"
      onClick={onTap}
      className="bg-boneWarm border border-hairline rounded-ios p-3 text-left hover:border-userRed"
      data-testid={testId}
    >
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1">{label}</p>
      <p className="text-3xl font-black font-heading tabular-nums text-graphite leading-none">
        {value}<span className="text-sm font-normal">{unit}</span>
      </p>
    </button>
  );
}

function ExercisePicker({ exercises, existingIds, onPick, onCustom, onClose, title = 'Add Exercise', allowExisting = false }) {
  const [q, setQ] = useState('');
  const filtered = exercises.filter(
    (e) => (allowExisting || !existingIds.includes(e.id)) &&
      (q === '' || e.name.toLowerCase().includes(q.toLowerCase()) || e.muscle_group.toLowerCase().includes(q.toLowerCase()))
  );
  const byGroup = filtered.reduce((acc, e) => {
    (acc[e.muscle_group] = acc[e.muscle_group] || []).push(e); return acc;
  }, {});
  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden max-h-[85vh] flex flex-col animate-slide-up">
        <div className="px-5 py-4 border-b border-hairline">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">{title}</h2>
            <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center" data-testid="close-picker-button">
              <X size={18} weight="bold" />
            </button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-graphite/40" />
            <input type="search" inputMode="search" enterKeyHint="search" autoCapitalize="none" autoCorrect="off"
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
              className="w-full h-12 pl-10 pr-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
              data-testid="exercise-search-input" autoFocus />
          </div>
          <button
            onClick={() => onCustom(q.trim())}
            className="mt-3 w-full h-12 border-2 border-dashed border-userRed/40 text-userRed font-bold text-xs uppercase tracking-[0.15em] rounded-ios-lg flex items-center justify-center gap-2"
            data-testid="add-custom-inline-button"
          >
            <Sparkle size={14} weight="fill" />
            {q.trim() ? `Create "${q.trim()}"` : 'Create Custom Exercise'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {Object.entries(byGroup).map(([g, exs]) => (
            <div key={g}>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">{g}</p>
              <div className="space-y-1">
                {exs.map((ex) => (
                  <button key={ex.id} onClick={() => onPick(ex)}
                    className="w-full text-left px-4 py-3 rounded-ios bg-boneWarm border border-hairline hover:border-graphite hover:bg-bone"
                    data-testid={`pick-exercise-${ex.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-graphite">{ex.name}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/50">{ex.equipment_type}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-center text-sm text-graphite/50 py-8">No matching exercises</p>}
        </div>
      </div>
    </div>
  );
}

function CustomExerciseModal({ initialName = '', onCreate, onClose }) {
  const [name, setName] = useState(initialName);
  const [muscleGroup, setMuscleGroup] = useState(MUSCLE_GROUPS[0]);
  const [equipmentType, setEquipmentType] = useState(EQUIPMENT_TYPES[0]);

  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up max-h-[85vh] flex flex-col">
        <div className="px-5 py-5 overflow-y-auto space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">Custom Exercise</h2>
            <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center" data-testid="close-custom-inline-button">
              <X size={18} weight="bold" />
            </button>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-base"
              placeholder="e.g. Landmine Press"
              data-testid="custom-inline-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">Type</label>
            <div className="flex gap-1.5 flex-wrap">
              {EQUIPMENT_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setEquipmentType(t)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    equipmentType === t ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`custom-inline-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">Muscle Group</label>
            <div className="flex gap-1.5 flex-wrap">
              {MUSCLE_GROUPS.map((m) => (
                <button
                  key={m}
                  onClick={() => setMuscleGroup(m)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    muscleGroup === m ? 'bg-userRed text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`custom-inline-muscle-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => name.trim() && onCreate({ name: name.trim(), muscle_group: muscleGroup, equipment_type: equipmentType })}
            disabled={!name.trim()}
            className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-40"
            data-testid="create-custom-inline-button"
          >
            Add To Session
          </button>
        </div>
      </div>
    </div>
  );
}
